import { useState } from 'react'
import {
  Currency,
  isSequenceApiError,
  isSequenceApiErrorResponse,
  useMutation
} from '@sequencehq/api'
import { dashboard20240730Client } from '@sequencehq/api/dashboard/v20240730'
import { useNotifications } from '@sequencehq/core-components'
import {
  mapApiResponseToFieldValidationErrors,
  useForm,
  ValidationError
} from '@sequencehq/utils'
import { apiDatesAdapters } from '@sequencehq/utils/dates'
import { required } from '@sequencehq/utils/validation'
import { match } from 'ts-pattern'
import {
  AMOUNT_VALIDATORS,
  COST_OF_CREDIT_VALIDATORS,
  CustomerCreditGrantFormValues,
  EXPIRY_DATE_VALIDATORS,
  NAME_VALIDATORS
} from './createCustomerCreditGrant.validation'

/**
 * Form state for a new customer credit grant, and the submit that creates it.
 * Returns `false` from `onSubmit` when the drawer should stay open.
 */
export const useCreateCustomerCreditGrantForm = ({
  customerId,
  enabledCurrencies
}: {
  customerId: string
  enabledCurrencies: Currency[]
}) => {
  const { displayNotification } = useNotifications()
  const { mutateAsync: postCustomerCreditGrant } = useMutation(
    dashboard20240730Client.postCustomerCreditGrant
  )
  const [showValidationErrors, setShowValidationErrors] = useState(false)
  const [apiErrors, setApiErrors] = useState<Record<
    string,
    ValidationError[]
  > | null>(null)

  // One key per open drawer. The platform only spends a key once a grant is
  // created, so a refused request can be corrected and resent under it, while
  // a resend after an unclear failure returns the grant rather than a second.
  const [idempotencyKey] = useState(() => crypto.randomUUID())

  const { fields, queries } = useForm<CustomerCreditGrantFormValues>({
    value: {
      currency: enabledCurrencies[0],
      amount: '',
      isCharged: false,
      costOfCredit: '',
      effectiveDate: apiDatesAdapters.toApi(new Date()),
      expiry: 'NEVER',
      expiryDate: '',
      name: ''
    },
    reinitializeOnValueChange: false,
    showValidationErrors,
    apiErrors,
    fieldConfiguration: [
      { property: 'currency', validation: [required] },
      { property: 'amount', validation: AMOUNT_VALIDATORS },
      { property: 'isCharged' },
      {
        property: 'costOfCredit',
        validation: COST_OF_CREDIT_VALIDATORS,
        // Hidden while the credit is free, and a hidden field must not block
        // submit.
        disabled: ({ formData }) => !formData.isCharged
      },
      { property: 'effectiveDate', validation: [required] },
      { property: 'expiry', validation: [required] },
      {
        property: 'expiryDate',
        validation: EXPIRY_DATE_VALIDATORS,
        disabled: ({ formData }) => formData.expiry !== 'ON_DATE'
      },
      { property: 'name', validation: NAME_VALIDATORS }
    ]
  })

  const onSubmit = async () => {
    setShowValidationErrors(true)

    if (!queries.isValid) {
      return false
    }

    setApiErrors(null)
    const trimmedName = fields.name.value.trim()

    try {
      await postCustomerCreditGrant({
        idempotencyKey,
        body: {
          customerId,
          name: trimmedName || undefined,
          currency: fields.currency.value,
          amount: Number.parseFloat(fields.amount.value),
          costOfCredit: fields.isCharged.value
            ? Number.parseFloat(fields.costOfCredit.value)
            : 0,
          effectiveDate: fields.effectiveDate.value,
          expiryDate: match(fields.expiry.value)
            .with('ON_DATE', () => fields.expiryDate.value)
            .with('NEVER', () => undefined)
            .exhaustive()
        }
      })
    } catch (e) {
      // Also reached when the connection drops, or a gateway answers with a
      // body that isn't JSON. Neither carries field errors, but the toast says
      // it failed, and resending reuses the key.
      displayNotification('Failed to add credit', { type: 'error' })
      if (isSequenceApiError(e) && isSequenceApiErrorResponse(e.response)) {
        setApiErrors(await mapApiResponseToFieldValidationErrors(e.response))
      }
      return false
    }

    displayNotification('Credit added', { type: 'success' })
    return true
  }

  return { fields, onSubmit }
}
